/**
 * Super Admin Badge Component
 * Shows when a super admin is viewing another portal
 * Allows quick return to super admin portal
 */

import React from 'react';
import { Box, Chip, IconButton, Tooltip, Avatar, alpha, useTheme } from '@mui/material';
import {
  Shield as ShieldIcon,
  ExitToApp as ExitIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  isInPortalSwitchMode,
  returnToSuperAdmin,
  getSuperAdminContext,
} from '../../utils/portalSwitcher';
import { clearPortalContext } from '../../../Features/Authentication/authSlice';

const SuperAdminBadge = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Check if we're in portal switch mode
  if (!isInPortalSwitchMode()) {
    return null;
  }

  const context = getSuperAdminContext();
  if (!context) {
    return null;
  }

  const handleReturn = () => {
    // Clear portal context in Redux
    dispatch(clearPortalContext());

    // Return to super admin portal
    returnToSuperAdmin(navigate);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 3,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
            : `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
        backdropFilter: 'blur(20px) saturate(180%)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`
            : `0 8px 32px ${alpha(theme.palette.grey[400], 0.2)}`,
        border: `2px solid ${theme.palette.error.main}`,
        animation: 'slideIn 0.3s ease-out',
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translateX(100px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          backgroundColor: theme.palette.error.main,
        }}
      >
        <ShieldIcon sx={{ fontSize: 20 }} />
      </Avatar>

      <Box>
        <Chip
          label="Super Admin Mode"
          size="small"
          color="error"
          icon={<AdminIcon />}
          sx={{
            fontWeight: 600,
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
        <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
          Viewing as: {context.originalUser?.username || 'Super Admin'}
        </Box>
      </Box>

      <Tooltip title="Return to Super Admin Portal">
        <IconButton
          onClick={handleReturn}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.2),
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ExitIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default SuperAdminBadge;
